import { Container, Graphics } from "@pixi/react";
import React from "react";
import { useSelector } from "react-redux";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import { usePrimaryUIToolbarDimensionsV2 } from "../../../../Hooks/usePrimaryUIToolbarDimensionsV2";
import ButtonPlay from "../Buttons/ButtonPlay";
import ButtonFreeSpinPlay from "../Buttons/ButtonFreeSpinPlay";
import ButtonHoldAndWinPlay from "../Buttons/ButtonHoldAndWinPlay";
import ButtonAutoplay from "../Buttons/ButtonAutoplay";
import ButtonBetSelect from "../Buttons/ButtonBetSelect";
import BalanceDisplay from "../Displays/BalanceDisplay";
import BetDisplay from "../Displays/BetDisplay";
import WinDisplay from "../Displays/WinDisplay";
import FreeRoundsWinDisplay from "../Displays/FreeRoundsWinDisplay";

import ButtonMenu from "../Buttons/ButtonMenu";
import ButtonSound from "../Buttons/ButtonSound";
import { selectHoldAndWinActive } from "../../../../State/selectors";
import RespinsDisplay from "../Displays/RespinsDisplay";
import { FEATURES_CONFIG } from "../../../../featuresConfig";
import ButtonBuyBonus from "../Buttons/ButtonBuyBonus";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../textureConfigs";
import {
  drawToolbarBackground,
  TOOLBAR_BACKGROUND_OPACITY,
} from "./ToolbarBackgrounds";

const bottomIsFront = FEATURES_CONFIG.TOOLBAR.BOTTOM.BACKGROUND_MODE === "front";

const ToolbarBottom = () => {
  const { dimensions, device, orientation, getResponsiveConfig } = useDimensions();

  // Get toolbar dimensions
  const toolbar = usePrimaryUIToolbarDimensionsV2();

  // Check if HoldAndWin feature is active
  const holdAndWinActive = useSelector(selectHoldAndWinActive);

  const isMobilePortrait =
    device === PixiSettings.DEVICE.MOBILE &&
    orientation === PixiSettings.ORIENTATION.PORTRAIT;

  // Button config for positioning Menu/Sound on the left side
  const buttonConfig = React.useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  const isDesktop = device === PixiSettings.DEVICE.DESKTOP;

  const bottomOpacity = isMobilePortrait ? 0.35 : TOOLBAR_BACKGROUND_OPACITY;

  // Desktop-only: both buttons on the left side
  const menuPositionDesktop = React.useMemo(() => ({
    x: buttonConfig.width * 0.8,
    y: toolbar.height / 2,
  }), [buttonConfig.width, toolbar.height]);

  const soundPositionDesktop = React.useMemo(() => ({
    x: buttonConfig.width * 0.8 + buttonConfig.width * 1.1,
    y: toolbar.height / 2,
  }), [buttonConfig.width, toolbar.height]);

  return (
    <Container
      name="secondary_toolbar_bottom"
      x={(dimensions.width - toolbar.width) / 2}
      y={
        isMobilePortrait
          ? dimensions.height - toolbar.height * 2 - 8
          : dimensions.height - toolbar.height
      }
      zIndex={10}
    >
      {/* Background — rendered here when mode is "front" (on top of game) */}
      {bottomIsFront && (
        <Graphics
          name="toolbar_bottom_bg_front"
          zIndex={0}
          draw={(g) =>
            drawToolbarBackground(g, toolbar.width, toolbar.height, bottomOpacity, FEATURES_CONFIG.TOOLBAR.BOTTOM.TOOLBAR_COLOR)
          }
        />
      )}

      <BalanceDisplay toolbar={toolbar} />
      <BetDisplay toolbar={toolbar} />
      <ButtonAutoplay toolbar={toolbar} />

      {FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && holdAndWinActive ? (
        <>
          <RespinsDisplay toolbar={toolbar} />
          <ButtonHoldAndWinPlay toolbar={toolbar} />
        </>
      ) : (
        <ButtonPlay toolbar={toolbar} />
      )}
      {FEATURES_CONFIG.FREE_SPINS.ENABLED && <ButtonFreeSpinPlay toolbar={toolbar} />}
      <ButtonBetSelect toolbar={toolbar} />
      {FEATURES_CONFIG.BONUS_BUY.ENABLED && <ButtonBuyBonus toolbar={toolbar} />}
      <WinDisplay toolbar={toolbar} />
      {FEATURES_CONFIG.FREE_ROUNDS.ENABLED && <FreeRoundsWinDisplay toolbar={toolbar} />}

      {/* Desktop: Menu + Sound both on left side */}
      {isDesktop && (
        <>
          <ButtonMenu toolbar={toolbar} position={menuPositionDesktop} />
          <ButtonSound toolbar={toolbar} position={soundPositionDesktop} />
        </>
      )}

      {/* Mobile landscape / Tablet: Sound left, Menu right (internal positioning) */}
      {!isMobilePortrait && !isDesktop && (
        <>
          <ButtonSound toolbar={toolbar} />
          <ButtonMenu toolbar={toolbar} />
        </>
      )}
    </Container>
  );
};

export default ToolbarBottom;
